import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import EmojiGridPicker from './EmojiGridPicker';
import usePopoverPosition from '../../hooks/usePopoverPosition';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '🎉'];
// 9 buttons (8 quick emoji + expand) x 28px + 8 gaps x 4px + 16px padding.
const COLLAPSED_WIDTH = 300;
// Fits emoji-picker-react's own width={320} prop plus the popover's padding.
const EXPANDED_WIDTH = 336;

/**
 * Discord-style reaction popover: a quick-bar of common emoji plus a "+" that
 * expands into the full emoji-picker-react grid. Positioned off `anchorRef`
 * via the shared usePopoverPosition hook (same recipe as CompactSelectPicker).
 *
 * `placement="auto"` (default) drops the popover below the anchor and flips
 * to grow upward when there isn't room below — used for the per-message
 * reaction trigger, which can sit anywhere in the scrollable message list.
 * `placement="top"` always anchors the popover's bottom edge just above the
 * anchor — used for the composer's emoji-insert button, which sits at the
 * bottom of the window where there's never room to grow down.
 */
export default function EmojiReactionPicker({ open, anchorRef, onClose, onSelect, placement = 'auto' }) {

  const [expanded, setExpanded] = useState(false);
  const popoverRef = useRef(null);


  const width = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
  // Clamp against the chat panel's own bounds, not the full window — the
  // chat column sits alongside other panels, so window.innerWidth is far
  // wider than what's actually visible here and doesn't prevent clipping.
  const bounds = useCallback(
    () => anchorRef.current?.closest('.chat-view')?.getBoundingClientRect() ?? null,
    [anchorRef]
  );
  // align 'end': right-align to the anchor's right edge (end of the action
  // bar, i.e. the delete icon) — matches Discord's placement and keeps the
  // popover inside the message row instead of drifting left past it.
  const pos = usePopoverPosition({ anchorRef, open, width, placement, align: 'end', bounds });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  if (!open || typeof document === 'undefined' || !document.body) return null;

  return createPortal(
    <>
      <div
        className="emoji-reaction-backdrop"
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose?.();
        }}
      />
      <div
        ref={popoverRef}
        className={`emoji-reaction-popover ${pos.dir === 'up' ? 'emoji-reaction-popover--up' : ''}`}
        style={{
          position: 'fixed',
          ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          overflowY: 'auto',
          zIndex: 10040,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="emoji-reaction-quickbar">
          {QUICK_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="emoji-reaction-quick-btn"
              title={emoji}
              onClick={() => onSelect?.(emoji)}
            >
              {emoji}
            </button>
          ))}
          <button
            type="button"
            className="emoji-reaction-expand-btn"
            title="More emoji"
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
          >
            <Plus size={15} />
          </button>
        </div>
        {expanded && <EmojiGridPicker onSelect={(e) => onSelect?.(e)} />}
      </div>
    </>,
    document.body
  );
}
